// The one event list for a requisition or a candidate (lane B10, slice S18; plan D12; DC-20). CR-094 "Requisition
// timeline: request through approval/posting/intake/panel/offer/filled/hold/cancel/reopen/archive." CR-093 "Candidate
// timeline: source/application through HRIS/start/disposition." BRD 4.3 "Show actor, date/time, source and linked
// artifacts subject to permission."
//
// It reads `timeline.view` and draws D0's History: per row the actor and the seats they hold on that requisition, his
// sentence for the kind (./sentences.ts), the date and time (in the row's own zone when it differs from the list's),
// the source tag and the permissioned artifact chips (./ArtifactChips.tsx). The server decides everything a reader
// sees: rows of a requisition the reader is not on are not sent and are not counted, a chip the reader could not open
// is not sent, and a reader refused the subject gets not-found, so the list is absent (DC-28), never a masked row.
//
// Mounted as the body of B2's Requisition timeline tab and B3's profile Candidate timeline tab (./register.ts, C-B10-13),
// which answer `#requisitions/<R>/timeline` and `#candidates/<cand_…>/timeline`, and by Home's timelines preview (S16)
// as <TimelineList compact limit={8} />. The full list follows the read's cursor up to MAX_ROWS (he has no word for a
// paging control yet: a debt in the S18 report).

import type { ReactNode } from "react";
import type { AppError } from "@/api/runtime";
import { timelineView, type TimelineRowOut, type TimelineViewInput, type TimelineViewOutput } from "@/api/tools";
import { useRead } from "@/api/useTool";
import {
  Card, DEFAULT_TIME_ZONE, ErrorStrip, History, HISTORY_LABELS, ROLE_LABELS, RULE_LABELS, formatTime, type HistoryEvent,
} from "@/mc";
import { artifactChips, TL_HS } from "./ArtifactChips";
import { SOURCE_LABELS } from "./labels";
import { hsKey } from "@/views/b10/lists/RowSignals";
import { SEPARATOR, ownEntry, sentenceOf, toneOfRow } from "./sentences";

export { TIMELINE_LABELS } from "./labels";

export type TimelineSubject = "requisition" | "candidate";

/** Home's short form (DC-20 `tev`): at most eight rows. */
export const COMPACT_ROWS = 8;
/** The read's own page size cap (`timeline.view` PAGE_MAX). */
export const PAGE_ROWS = 200;
/** The full list follows the cursor up to this many rows. */
export const MAX_ROWS = 1000;

export interface TimelineListProps {
  subject: TimelineSubject;
  /** The requisition id, or the candidate's id. */
  id: string;
  /** At most this many rows (compact: default 8; full: every row, up to MAX_ROWS). */
  limit?: number | undefined;
  /** Only these timeline kinds (each one of the spine's timeline vocabulary). */
  kinds?: readonly string[] | undefined;
  /** Home's short form: one line a row, no day rules, no seat words. */
  compact?: boolean | undefined;
  /** The hotspot prefix of the list's controls (default `tl`). */
  hsPrefix?: string | undefined;
  /** Draws the list in a D0 Card with this title; the card is absent with the list. */
  title?: ReactNode | undefined;
}

type Query = Pick<TimelineListProps, "subject" | "id" | "limit" | "kinds" | "compact">;

/** The first page's input: the subject, the kinds asked for (none = every kind), the page size. */
export function timelineInput({ subject, id, limit, kinds, compact }: Query, cursor: string | null = null): TimelineViewInput {
  const wanted = limit ?? (compact ? COMPACT_ROWS : PAGE_ROWS);
  return {
    subject_type: subject,
    id,
    kinds: kinds && kinds.length > 0 ? [...kinds] : null,
    limit: Math.max(1, Math.min(wanted, PAGE_ROWS)),
    cursor,
  };
}

/** How many rows the list shows at most. */
export function rowCap({ limit, compact }: Pick<Query, "limit" | "compact">): number {
  return Math.max(1, Math.min(limit ?? (compact ? COMPACT_ROWS : MAX_ROWS), MAX_ROWS));
}

/** The cache key of one list: the same subject, id, kinds and size share one read. */
export function timelineKey({ subject, id, limit, kinds, compact }: Query): readonly unknown[] {
  return ["timeline", subject, id, kinds && kinds.length > 0 ? [...kinds].sort().join(",") : "", limit ?? 0, compact ? "compact" : "full"];
}

/**
 * Reads pages until the server says there is no further row this reader may see, or `max` rows are in hand. Each page
 * is one audited read; a cursor the server repeats ends the walk.
 */
export async function collectRows(
  fetchPage: (input: TimelineViewInput) => Promise<TimelineViewOutput>,
  first: TimelineViewInput,
  max: number,
): Promise<{ rows: TimelineRowOut[]; more: boolean }> {
  const rows: TimelineRowOut[] = [];
  let input = first;
  for (;;) {
    const page = await fetchPage(input);
    rows.push(...page.rows);
    const next = page.next_cursor;
    if (!next || rows.length >= max || next === input.cursor) return { rows: rows.slice(0, max), more: Boolean(next) || rows.length > max };
    input = { ...input, cursor: next };
  }
}

/** A refused subject is absent, not an error (DC-28): not-found, not permitted, not signed in. */
export function isAbsent(error: Pick<AppError, "status"> | null | undefined): boolean {
  return !!error && (error.status === 401 || error.status === 403 || error.status === 404);
}

/** A refused CANDIDATE subject in the profile's full tab: the reader has already opened the profile, so the refusal says
 * only that no line is theirs to read (a candidate with no application yet, or none on a requisition they are on); the
 * tab draws the empty timeline, never a blank tab (demo smoke D10). A refused requisition and every compact preview
 * stay absent (DC-28). */
export function emptyWhenRefused(query: { subject: "requisition" | "candidate"; compact?: boolean | undefined }): boolean {
  return query.subject === "candidate" && !query.compact;
}

const SEAT_WORDS: ReadonlySet<string> = new Set(Object.values(ROLE_LABELS));

/** The actor's seat words the row carries, his words only (the legacy alias and the system are not drawn). */
export function seatWords(row: Pick<TimelineRowOut, "actor_roles">): string[] {
  return (row.actor_roles ?? []).filter((word) => SEAT_WORDS.has(word));
}

function zoneOk(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The list's zone: the one asked for, else the first row's own, else the design system's default. */
export function listZone(rows: readonly Pick<TimelineRowOut, "tz">[], timeZone?: string): string {
  if (zoneOk(timeZone)) return timeZone;
  const own = rows.find((row) => zoneOk(row.tz))?.tz;
  return own ?? DEFAULT_TIME_ZONE;
}

/** A row's source tag: his word for its source, and its own time and zone when that zone is not the list's. */
export function sourceTag(row: Pick<TimelineRowOut, "source" | "tz" | "at">, zone: string): string | undefined {
  const word = ownEntry(SOURCE_LABELS as Readonly<Record<string, string>>, row.source);
  const own = zoneOk(row.tz) && row.tz !== zone ? formatTime(row.at, { timeZone: row.tz, zone: true }) : undefined;
  if (word && own) return `${word}${SEPARATOR}${own}`;
  return word ?? own;
}

export interface EventOptions {
  hsPrefix?: string | undefined;
  compact?: boolean | undefined;
  timeZone: string;
}

/** The rows as D0 History events, newest first as the server sent them. */
export function toHistoryEvents(rows: readonly TimelineRowOut[], { hsPrefix = TL_HS, compact, timeZone }: EventOptions): HistoryEvent[] {
  return rows.map((row) => {
    const sentence = sentenceOf(row);
    const seats = compact ? [] : seatWords(row);
    return {
      id: row.id,
      actor: row.actor_name ? { name: row.actor_name, hueKey: row.actor_id ?? undefined } : undefined,
      sentence: seats.length > 0 ? <>{sentence}<span className="mc-t3">{["", ...seats].join(SEPARATOR)}</span></> : sentence,
      at: row.at,
      tone: toneOfRow(row),
      source: compact ? undefined : sourceTag(row, timeZone),
      artifacts: compact ? [] : artifactChips(row.artifacts, hsPrefix),
    };
  });
}

export interface TimelineRowsProps {
  rows: readonly TimelineRowOut[];
  subject: TimelineSubject;
  compact?: boolean | undefined;
  limit?: number | undefined;
  hsPrefix?: string | undefined;
  loading?: boolean | undefined;
  timeZone?: string | undefined;
}

/** The list itself, from rows already read: the part a test, a gallery cell or a host with its own read draws. */
export function TimelineRows({ rows, subject, compact, limit, hsPrefix = TL_HS, loading, timeZone }: TimelineRowsProps) {
  const zone = listZone(rows, timeZone);
  const label = subject === "candidate" ? HISTORY_LABELS.candidateTimeline : HISTORY_LABELS.requisitionTimeline;
  return (
    <History
      label={label}
      events={toHistoryEvents(rows, { hsPrefix, compact, timeZone: zone })}
      compact={compact}
      max={compact ? rowCap({ limit, compact }) : undefined}
      loading={loading}
      timeZone={zone}
    />
  );
}

/** The requisition's or the candidate's timeline, read for the signed-in reader. */
export function TimelineList({ subject, id, limit, kinds, compact, hsPrefix = TL_HS, title }: TimelineListProps) {
  const query: Query = { subject, id, limit, kinds, compact };
  const q = useRead(timelineKey(query), () => collectRows((input) => timelineView(input), timelineInput(query), rowCap(query)), {
    enabled: id !== "",
  });
  if (id === "") return null;
  if (isAbsent(q.error)) {
    if (!emptyWhenRefused(query)) return null;
    const empty = <TimelineRows rows={[]} subject={subject} compact={compact} limit={limit} hsPrefix={hsPrefix} />;
    return title === undefined ? empty : <Card title={title}>{empty}</Card>;
  }
  const body = q.error ? (
    <ErrorStrip
      message={q.error.remedy || RULE_LABELS.failedSyncRetryable}
      problem={{ status: q.error.status, refusal_code: q.error.code, detail: q.error.message, request_id: q.error.requestId }}
      onRetry={() => void q.refetch()}
      retrying={q.isFetching}
      hs={hsKey(hsPrefix, "retry")}
    />
  ) : (
    <TimelineRows rows={q.data?.rows ?? []} subject={subject} compact={compact} limit={limit} hsPrefix={hsPrefix} loading={q.isPending} />
  );
  return title === undefined ? body : <Card title={title}>{body}</Card>;
}
